import type React from 'react';
import { useEffect, useMemo } from 'react';

import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { tools as factoryTools } from 'tods-competition-factory';

import {
  EventsPanel,
  HomePage,
  HomePageLegacy,
  LeagueEventsPanel,
  LeaguesHomePage,
  LinkedTournamentsPage,
  LoginPage,
  MatchesPanel,
  NotFoundPage,
  ParticipantsPanel,
  ReportsPanel,
  SafePlayPage,
  SchedulePanel,
  SelectedEventPanel,
  SplitDrawsContainer,
  TournamentPage,
} from 'components/Lazyload';
import DrawPanel from 'components/panels/eventsSplitDraws/selectedEvent/DrawPanel';
import SelectedTeamPanel from 'components/panels/team/SelectedTeamPanel';
import { getEnvConfig } from 'config/environment';
import { ENABLE_REPORTS } from 'config/flags/featureFlag';
import {
  ADD_FACILITY,
  AUTO_SCHEDULE,
  COURT_CONFIG,
  DRAW_PART,
  DRAWS_PART,
  EDIT_FACILITY,
  EVENT_PART,
  EVENTS_PART,
  FACILITIES,
  HOME,
  LEAGUES,
  LINKED_TOURNAMENTS_STRING,
  LOGIN,
  MATCHES,
  NOT_FOUND,
  PLAYERS_PART,
  REPORTS_PART,
  SAFE_PLAY,
  SCHEDULE,
  SCHEDULE_SETTINGS,
  TEAM_PART,
  TEAMS_PART,
  TOURNAMENT_LEAGUE,
} from 'config/router/constants';
import EntriesPanel from 'containers/eventsSplitDraws/entries/EntriesPanel';
import useIsLeagueDesk from 'hooks/leagues/useIsLeagueDesk';
import { usePrevious } from 'hooks/usePrevious';
import { useSwitchTheme } from 'hooks/useSwitchTheme';
import PrivateOutlet from 'router/PrivateOutlet';
import { ThemeNameEnum } from 'typedefs/themeTypes';
import { isStandalone } from 'utils/environment';
import { isLocalhost } from 'utils/isLocalhost';

import LeagueOutlet from './LeagueOutlet';
import SafePlayOutlet from './SafePlayOutlet';

const { UUID } = factoryTools;

const MainRouter: React.FC<React.PropsWithChildren<unknown>> = () => {
  const location = useLocation();
  const prevLocation = usePrevious(location);
  const isStandAloneDemo = isStandalone();
  const [searchParams] = useSearchParams();
  const { themeName, setThemeName } = useSwitchTheme();
  const legacyUI = themeName === ThemeNameEnum.V1;
  const HomePageComponent = legacyUI ? HomePageLegacy : HomePage;
  const legacyUIParam = searchParams.has('legacyUI');
  const isLeagueDesk = useIsLeagueDesk();

  useEffect(() => {
    if (!themeName) {
      setThemeName(ThemeNameEnum.V2);
    } else if (legacyUIParam) {
      setThemeName(ThemeNameEnum.V1);
    }
  }, [legacyUIParam, setThemeName, themeName]);

  useEffect(() => {
    TournamentPage.preload();
    HomePageComponent.preload();
    LeaguesHomePage.preload();
    LoginPage.preload();
    NotFoundPage.preload();
    SafePlayPage.preload();
    SplitDrawsContainer.preload();
  }, [HomePageComponent]);

  useEffect(() => {
    const { pathname } = location;

    if (!isLocalhost) {
      if (pathname !== prevLocation?.pathname && (window as any).Appcues?.page) {
        (window as any).Appcues.page();
      }
      if (isStandAloneDemo) {
        let uuid = localStorage.getItem('tmxuuid');
        if (!uuid) {
          uuid = `u_${UUID()}`;
          localStorage.setItem('tmxuuid', uuid);
        }
        if ((window as any).Appcues?.identify) {
          // (window as any).Appcues.identify(uuid);
          // Will Low requested removal of uuid
          (window as any).Appcues.anonymous();
        }
      } else if ((window as any).Appcues?.anonymous) {
        (window as any).Appcues.anonymous();
      }
    }
  }, [location, prevLocation, isStandAloneDemo]);

  const sharedLeagueDeskTDeskRoutes = useMemo(
    () => (
      <Route element={<TournamentPage />} path={TOURNAMENT_LEAGUE}>
        <Route index element={<Navigate replace to={PLAYERS_PART} />} />
        <Route element={<ParticipantsPanel />} path={PLAYERS_PART} />
        <Route element={<LinkedTournamentsPage />} path={LINKED_TOURNAMENTS_STRING} />
        <Route path={TEAMS_PART}>
          <Route index element={<ParticipantsPanel />} />
          <Route element={<SelectedTeamPanel />} path={TEAM_PART} />
        </Route>
        <Route path={EVENTS_PART}>
          {isLeagueDesk ? <Route index element={<LeagueEventsPanel />} /> : <Route index element={<EventsPanel />} />}
          <Route element={<SelectedEventPanel />} path={EVENT_PART}>
            <Route index element={<EntriesPanel />} />
            <Route path={DRAWS_PART}>
              <Route index element={<DrawPanel />} />
              <Route element={<SplitDrawsContainer />} path={DRAW_PART} />
            </Route>
          </Route>
        </Route>
        <Route path={MATCHES}>
          <Route index element={<MatchesPanel />} />
        </Route>
        <Route path={SCHEDULE}>
          <Route index element={<SchedulePanel />} />
          <Route element={<SchedulePanel />} path={AUTO_SCHEDULE} />
          <Route element={<SchedulePanel />} path={FACILITIES} />
          <Route element={<SchedulePanel />} path={ADD_FACILITY} />
          <Route element={<SchedulePanel />} path={EDIT_FACILITY} />
          <Route element={<SchedulePanel />} path={COURT_CONFIG} />
          <Route element={<SchedulePanel />} path={SCHEDULE} />
          <Route element={<SchedulePanel />} path={SCHEDULE_SETTINGS} />
        </Route>
        {getEnvConfig()[ENABLE_REPORTS] === 'true' && <Route element={<ReportsPanel />} path={REPORTS_PART} />}
      </Route>
    ),
    [isLeagueDesk],
  );

  return (
    <div>
      <Routes>
        <Route element={<LoginPage />} path={LOGIN} />
        <Route element={<PrivateOutlet />}>
          <Route element={<HomePageComponent />} path={HOME} />
          <Route element={<SafePlayOutlet />}>{sharedLeagueDeskTDeskRoutes}</Route>
          <Route element={<LeagueOutlet />}>
            <Route element={<LeaguesHomePage />} path={LEAGUES} />
            <Route element={<SafePlayOutlet />}>{sharedLeagueDeskTDeskRoutes}</Route>
          </Route>
          <Route element={<SafePlayPage />} path={`${SAFE_PLAY}/*`} />
        </Route>
        <Route element={<NotFoundPage />} path={NOT_FOUND} />
        <Route element={<Navigate replace to={NOT_FOUND} />} path="*"></Route>
      </Routes>
    </div>
  );
};

export default MainRouter;
